import React, { useState, useEffect } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';

// MUI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';

function HelpLogs() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedLog, setSelectedLog] = useState(null);
    const accesstoken = sessionStorage.getItem('accesstoken');

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`/api/help/logs?accesstoken=${accesstoken}`);
                setLogs(data.logs || []);
            } catch (e) {
                console.error('Failed to load help logs', e);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant='h6' sx={{ mb: 2, fontWeight: 700 }}>
                AI Assistant Conversations ({logs.length})
            </Typography>
            <TableContainer component={Paper}>
                <Table size='small'>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Workspace</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Messages</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>First Question</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {logs.map(log => {
                            const userMessages = (log.messages || []).filter(m => m.role === 'user');
                            const firstQuestion = userMessages[0]?.content || '—';
                            return (
                                <TableRow
                                    key={log.id}
                                    hover
                                    sx={{ cursor: 'pointer', '& td': { fontSize: '12px' } }}
                                    onClick={() => setSelectedLog(log)}
                                >
                                    <TableCell>{dayjs(log.timestamp).format('MMM D, YYYY h:mm A')}</TableCell>
                                    <TableCell>{log.organizationName || log.organizationId}</TableCell>
                                    <TableCell>{log.email}</TableCell>
                                    <TableCell>
                                        <Chip label={`${(log.messages || []).length} msgs`} size='small' sx={{ fontSize: '10px', height: '20px' }} />
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {firstQuestion.substring(0, 80)}{firstQuestion.length > 80 ? '...' : ''}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {selectedLog && (
                <Dialog open={true} onClose={() => setSelectedLog(null)} maxWidth='md' fullWidth>
                    <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                            <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
                                {selectedLog.email} — {selectedLog.organizationName}
                            </Typography>
                            <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                                {dayjs(selectedLog.timestamp).format('MMMM D, YYYY [at] h:mm A')}
                            </Typography>
                        </Box>
                        <Button size='small' onClick={() => setSelectedLog(null)}>Close</Button>
                    </DialogTitle>
                    <DialogContent>
                        {(selectedLog.messages || []).map((msg, i) => (
                            <Box key={i} sx={{
                                display: 'flex',
                                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                                mb: 1.5,
                            }}>
                                <Box sx={{
                                    maxWidth: '80%',
                                    p: 1.5,
                                    bgcolor: msg.role === 'user' ? 'primary.main' : 'grey.100',
                                    color: msg.role === 'user' ? 'white' : 'text.primary',
                                    borderRadius: '12px',
                                    borderBottomRightRadius: msg.role === 'user' ? '4px' : '12px',
                                    borderBottomLeftRadius: msg.role === 'user' ? '12px' : '4px',
                                    fontSize: '13px',
                                    lineHeight: 1.5,
                                    '& p': { margin: '4px 0' },
                                    '& ol, & ul': { margin: '4px 0', paddingLeft: '20px' },
                                }}>
                                    {msg.role === 'assistant' ? (
                                        <div dangerouslySetInnerHTML={{ __html: msg.content }} />
                                    ) : (
                                        msg.content
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </DialogContent>
                </Dialog>
            )}
        </Box>
    );
}

export default HelpLogs;
